import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { formatPlanning } from "utils/planning";

const MATIERES = {
  quoran: { label: "Qour'an", bg: "#e8f5e9", color: "#2e7d32" },
  arabe: { label: "Langue arabe", bg: "#e3f2fd", color: "#1565c0" },
  science: { label: "Sciences de la religion", bg: "#fff3e0", color: "#e65100" },
};

function MatiereBadge({ value }) {
  const m = MATIERES[value] || { label: value || "—", bg: "#f5f5f5", color: "#333" };
  return (
    <MDBox sx={{
      display: "inline-block", padding: "3px 12px",
      borderRadius: "12px", background: m.bg, color: m.color,
      fontSize: 12, fontWeight: 600,
    }}>
      {m.label}
    </MDBox>
  );
}

function Cours() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [cours, setCours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterMatiere, setFilterMatiere] = useState("");
  const [onglet, setOnglet] = useState("actifs"); // actifs | general | termines
  const showTermines = onglet === "termines";
  const isMobile = window.innerWidth < 768;
  const [viewMode, setViewMode] = useState(isMobile ? "cards" : "table");

  useEffect(() => { fetchCours(); }, [onglet]);

  const fetchCours = async () => {
    setLoading(true);
    try {
      const url = { actifs: "/cours", general: "/cours?type=general", termines: "/cours?termine=true" }[onglet];
      const { data } = await api.get(url);
      // Les cours généraux / conférences ont leur propre onglet
      setCours(onglet === "actifs" ? data.filter((c) => c.type !== "general") : data);
    } catch (error) {
      console.error("Erreur:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce cours ?")) return;
    try {
      await api.delete(`/cours/${id}`);
      fetchCours();
    } catch (error) {
      alert(error.response?.data?.message || "Erreur lors de la suppression");
    }
  };

  const filtered = cours.filter((c) => {
    const matchSearch = search === "" ||
      `${c.eleve?.prenom} ${c.eleve?.nom}`.toLowerCase().includes(search.toLowerCase()) ||
      c.groupe?.nom?.toLowerCase().includes(search.toLowerCase()) ||
      c.commentaire_general?.toLowerCase().includes(search.toLowerCase()) ||
      `${c.enseignant?.prenom} ${c.enseignant?.nom}`.toLowerCase().includes(search.toLowerCase()) ||
      c.lieu?.toLowerCase().includes(search.toLowerCase());
    const matchMatiere = filterMatiere === "" || c.matiere === filterMatiere;
    return matchSearch && matchMatiere;
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(46,125,50,0.4)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 2,
              }}>
                <MDBox>
                  <MDTypography variant="h5" color="white" fontWeight="bold">
                    Gestion des Cours
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
                    {onglet === "general"
                      ? `${cours.length} cours général${cours.length > 1 ? "aux" : ""} / conférence${cours.length > 1 ? "s" : ""}`
                      : `${cours.length} cours ${showTermines ? "terminé" : "actif"}${cours.length > 1 ? "s" : ""}`}
                  </MDTypography>
                </MDBox>
                <MDButton variant="contained" size="medium"
                  onClick={() => navigate("/cours/nouveau")}
                  sx={{
                    background: "#fff", color: "#2e7d32",
                    fontWeight: 700, borderRadius: "10px",
                    "&:hover": { background: "#e8f5e9" },
                  }}>
                  <Icon sx={{ mr: { xs: 0, sm: 0.5 } }}>add</Icon>
                  <MDBox component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                    Planifier un cours
                  </MDBox>
                </MDButton>
              </MDBox>

              <MDBox pt={3} px={3} pb={3}>

                {/* Onglets actifs / généraux / terminés */}
                <MDBox display="flex" gap={1} mb={2} flexWrap="wrap">
                  {[
                    ["actifs", "Cours actifs", "play_circle"],
                    ["general", "Généraux / Conférences", "campaign"],
                    ["termines", "Cours terminés", "check_circle"],
                  ].map(([val, label, icon]) => (
                    <MDBox key={label} onClick={() => setOnglet(val)} sx={{
                      display: "flex", alignItems: "center", gap: 0.5,
                      padding: "6px 14px", borderRadius: "10px", cursor: "pointer",
                      border: "1px solid", fontSize: 13, fontWeight: 600,
                      borderColor: onglet === val ? "#2e7d32" : "#d2d6da",
                      background: onglet === val ? "#2e7d32" : "#fff",
                      color: onglet === val ? "#fff" : "#344767",
                      transition: "all 0.2s",
                    }}>
                      <Icon sx={{ fontSize: 16 }}>{icon}</Icon>
                      {label}
                    </MDBox>
                  ))}
                </MDBox>

                {/* Filtres */}
                <MDBox display="flex" justifyContent="space-between"
                  alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                  <MDBox sx={{ flex: 1, maxWidth: { xs: "100%", sm: 360 } }}>
                    <MDInput fullWidth
                      label="🔍  Rechercher par élève, enseignant, lieu..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                    />
                  </MDBox>
                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <MDBox onClick={() => setFilterMatiere("")} sx={{
                      padding: "6px 14px", borderRadius: "20px", cursor: "pointer",
                      border: "1px solid", fontSize: 13, fontWeight: 500,
                      borderColor: filterMatiere === "" ? "#2e7d32" : "#d2d6da",
                      background: filterMatiere === "" ? "#2e7d32" : "#fff",
                      color: filterMatiere === "" ? "#fff" : "#344767",
                      transition: "all 0.2s",
                    }}>
                      Tous
                    </MDBox>
                    {Object.entries(MATIERES).map(([key, m]) => (
                      <MDBox key={key} onClick={() => setFilterMatiere(key)} sx={{
                        padding: "6px 14px", borderRadius: "20px", cursor: "pointer",
                        border: "1px solid", fontSize: { xs: 11, sm: 13 }, fontWeight: 500,
                        borderColor: filterMatiere === key ? m.color : "#d2d6da",
                        background: filterMatiere === key ? m.color : "#fff",
                        color: filterMatiere === key ? "#fff" : "#344767",
                        transition: "all 0.2s",
                      }}>
                        {isMobile ? m.label.split(" ")[0] : m.label}
                      </MDBox>
                    ))}
                    <Tooltip title="Vue tableau">
                      <MDBox onClick={() => setViewMode("table")} sx={{
                        width: 36, height: 36, borderRadius: "8px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: viewMode === "table" ? "#2e7d32" : "#f5f5f5",
                        color: viewMode === "table" ? "#fff" : "#757575",
                        transition: "all 0.2s",
                      }}>
                        <Icon fontSize="small">table_rows</Icon>
                      </MDBox>
                    </Tooltip>
                    <Tooltip title="Vue cartes">
                      <MDBox onClick={() => setViewMode("cards")} sx={{
                        width: 36, height: 36, borderRadius: "8px", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: viewMode === "cards" ? "#2e7d32" : "#f5f5f5",
                        color: viewMode === "cards" ? "#fff" : "#757575",
                        transition: "all 0.2s",
                      }}>
                        <Icon fontSize="small">grid_view</Icon>
                      </MDBox>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                  </MDBox>
                ) : filtered.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>menu_book</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      Aucun cours trouvé
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {search ? "Essayez une autre recherche"
                        : showTermines ? "Aucun cours terminé"
                          : onglet === "general" ? "Aucun cours général / conférence"
                            : "Commencez par planifier un cours"}
                    </MDTypography>
                  </MDBox>

                ) : viewMode === "table" ? (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          {[onglet === "general" ? "Participants" : "Élève", "Enseignant", "Matière", "Lieu", "Horaire", "Statut", "Actions"].map((h) => (
                            <th key={h} style={{
                              padding: "10px 16px", textAlign: "left",
                              fontSize: 11, fontWeight: 700, color: "#9e9e9e",
                              textTransform: "uppercase", letterSpacing: "0.05em",
                              borderBottom: "2px solid #f5f5f5",
                            }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((c) => (
                          <tr key={c.id}
                            style={{
                              borderBottom: "1px solid #fafafa",
                              ...(c.chapitre_termine && { opacity: 0.6, fontStyle: "italic" }),
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = "#f8fff8")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                {c.type === "general"
                                  ? `🎤 Général / Conférence${c.nb_participants != null ? ` (${c.nb_participants} pers.)` : ""}`
                                  : c.eleve ? `${c.eleve.prenom} ${c.eleve.nom}` : c.groupe?.nom || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {c.enseignant?.prenom} {c.enseignant?.nom}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MatiereBadge value={c.matiere} />
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {c.lieu || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDTypography variant="caption" sx={{ color: "#7b809a" }}>
                                {formatPlanning(c) || "—"}
                              </MDTypography>
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              {c.chapitre_termine ? (
                                <MDBox sx={{
                                  display: "inline-block", padding: "3px 12px",
                                  borderRadius: "12px", background: "#e8f5e9",
                                  color: "#2e7d32", fontSize: 12, fontWeight: 600,
                                }}>✓ Terminé</MDBox>
                              ) : (
                                <MDBox sx={{
                                  display: "inline-block", padding: "3px 12px",
                                  borderRadius: "12px", background: "#fff3e0",
                                  color: "#e65100", fontSize: 12, fontWeight: 600,
                                }}>En cours</MDBox>
                              )}
                            </td>
                            <td style={{ padding: "14px 16px" }}>
                              <MDBox display="flex" gap={1}>
                                <Tooltip title="Voir le détail">
                                  <MDBox onClick={() => navigate(`/cours/${c.id}`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#e8f5e9", color: "#2e7d32",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#c8e6c9" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>visibility</Icon>
                                  </MDBox>
                                </Tooltip>
                                <Tooltip title="Modifier">
                                  <MDBox onClick={() => navigate(`/cours/${c.id}/modifier`)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#fff8e1", color: "#f57f17",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffecb3" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>edit</Icon>
                                  </MDBox>
                                </Tooltip>
                                <Tooltip title="Supprimer">
                                  <MDBox onClick={() => handleDelete(c.id)} sx={{
                                    width: 32, height: 32, borderRadius: "8px",
                                    background: "#ffebee", color: "#c62828",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", "&:hover": { background: "#ffcdd2" },
                                  }}>
                                    <Icon sx={{ fontSize: 16 }}>cancel</Icon>
                                  </MDBox>
                                </Tooltip>
                              </MDBox>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </MDBox>

                ) : (
                  <Grid container spacing={2}>
                    {filtered.map((c) => (
                      <Grid item xs={12} sm={6} md={4} key={c.id}>
                        <MDBox sx={{
                          background: "#fff", borderRadius: "16px",
                          border: "1px solid #f0f0f0", overflow: "hidden",
                          transition: "all 0.2s",
                          ...(c.chapitre_termine && { opacity: 0.6, fontStyle: "italic" }),
                          "&:hover": { boxShadow: "0 8px 24px rgba(0,0,0,0.1)", transform: "translateY(-2px)" },
                        }}>
                          <MDBox px={2} py={1.5} sx={{
                            background: MATIERES[c.matiere]?.bg || "#f5f5f5",
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                          }}>
                            <MatiereBadge value={c.matiere} />
                            {c.chapitre_termine ? (
                              <MDBox sx={{
                                padding: "3px 10px", borderRadius: "12px",
                                background: "#e8f5e9", color: "#2e7d32",
                                fontSize: 11, fontWeight: 600,
                              }}>✓ Terminé</MDBox>
                            ) : (
                              <MDBox sx={{
                                padding: "3px 10px", borderRadius: "12px",
                                background: "#fff3e0", color: "#e65100",
                                fontSize: 11, fontWeight: 600,
                              }}>En cours</MDBox>
                            )}
                          </MDBox>
                          <MDBox p={2}>
                            <MDTypography variant="button" fontWeight="bold"
                              sx={{ color: "#344767", display: "block", mb: 0.5 }}>
                              {c.type === "general"
                                ? `🎤 Général / Conférence${c.nb_participants != null ? ` (${c.nb_participants} pers.)` : ""}`
                                : `👨‍🎓 ${c.eleve ? `${c.eleve.prenom} ${c.eleve.nom}` : `👥 ${c.groupe?.nom}`}`}
                            </MDTypography>
                            <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                              👨‍🏫 {c.enseignant?.prenom} {c.enseignant?.nom}
                            </MDTypography>
                            {c.lieu && (
                              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                                📍 {c.lieu}
                              </MDTypography>
                            )}
                            {formatPlanning(c) && (
                              <MDTypography variant="caption" color="text" display="block" mb={0.5}>
                                🕐 {formatPlanning(c)}
                              </MDTypography>
                            )}
                          </MDBox>
                          <MDBox display="flex" gap={1} px={2} pb={2}
                            onClick={(e) => e.stopPropagation()}>
                            <MDBox onClick={() => navigate(`/cours/${c.id}`)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#e8f5e9", color: "#2e7d32",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#c8e6c9" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>visibility</Icon> Voir
                            </MDBox>
                            <MDBox onClick={() => navigate(`/cours/${c.id}/modifier`)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#fff8e1", color: "#f57f17",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffecb3" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>edit</Icon> Modifier
                            </MDBox>
                            <MDBox onClick={() => handleDelete(c.id)} sx={{
                              flex: 1, padding: "6px", borderRadius: "8px",
                              background: "#ffebee", color: "#c62828",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", fontSize: 12, fontWeight: 600, gap: 0.5,
                              "&:hover": { background: "#ffcdd2" },
                            }}>
                              <Icon sx={{ fontSize: 14 }}>cancel</Icon> Supprimer
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Cours;